import type { ParsedSequence } from "@formats/alignment";
import type { ParseError } from "@lib/errors";

export type { ParsedSequence } from "@formats/alignment";
export type { ParseError } from "@lib/errors";

const VALID_NUCLEOTIDES = new Set([
  "A",
  "T",
  "G",
  "C",
  "U",
  "R",
  "Y",
  "S",
  "W",
  "K",
  "M",
  "B",
  "D",
  "H",
  "V",
  "N",
]);

/** The result of parsing user-provided sequence input. */
export type ParseResult = {
  format: "fasta" | "plain";
  sequences: ParsedSequence[];
  errors: ParseError[];
};

/**
 * Validate a sequence string against IUPAC nucleotide codes.
 *
 * @param sequence - the sequence to validate
 * @param lineOffset - line number offset for error reporting
 * @returns array of validation errors
 */
function validateSequence(sequence: string, lineOffset: number): ParseError[] {
  const errors: ParseError[] = [];
  const upper = sequence.toUpperCase();

  for (let i = 0; i < upper.length; i++) {
    const char = upper[i];
    if (!VALID_NUCLEOTIDES.has(char)) {
      errors.push({
        line: lineOffset,
        message: `Invalid character '${char}'`,
      });
    }
  }

  return errors;
}

/**
 * Parse FASTA formatted input into sequences.
 *
 * @param input - FASTA formatted string
 * @returns parse result with sequences and errors
 */
function parseFasta(input: string): ParseResult {
  const sequences: ParsedSequence[] = [];
  const errors: ParseError[] = [];
  const lines = input.split("\n");

  let currentName: string | null = null;
  let currentSequence = "";
  let sequenceStartLine = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith(">")) {
      if (currentName !== null && currentSequence) {
        const cleaned = currentSequence.replace(/\s/g, "").toUpperCase();
        errors.push(...validateSequence(cleaned, sequenceStartLine));
        sequences.push({
          id: crypto.randomUUID(),
          name: currentName,
          sequence: cleaned,
        });
      }

      currentName = line.slice(1).trim() || `Sequence ${sequences.length + 1}`;
      currentSequence = "";
      sequenceStartLine = i + 1;
    } else if (line && currentName !== null) {
      currentSequence += line;
    }
  }

  if (currentName !== null && currentSequence) {
    const cleaned = currentSequence.replace(/\s/g, "").toUpperCase();
    errors.push(...validateSequence(cleaned, sequenceStartLine));
    sequences.push({
      id: crypto.randomUUID(),
      name: currentName,
      sequence: cleaned,
    });
  }

  return { format: "fasta", sequences, errors };
}

/**
 * Parse plain sequence input (no FASTA headers).
 *
 * @param input - plain sequence string
 * @param name - name for the sequence
 * @returns parse result with single sequence and errors
 */
function parsePlain(input: string, name: string): ParseResult {
  const cleaned = input.replace(/\s/g, "").toUpperCase();
  const errors = validateSequence(cleaned, 1);

  const sequences: ParsedSequence[] = cleaned
    ? [{ id: crypto.randomUUID(), name, sequence: cleaned }]
    : [];

  return { format: "plain", sequences, errors };
}

/**
 * Detect if input is FASTA format.
 *
 * @param input - the input string to check
 * @returns true if input appears to be FASTA format
 */
export function isFasta(input: string): boolean {
  return input.trimStart().startsWith(">");
}

/**
 * Parse sequence input, auto-detecting format.
 *
 * @param input - sequence input (FASTA or plain text)
 * @param name - name for plain text sequences (ignored for FASTA)
 * @returns parse result with sequences and errors
 */
export function parseSequenceInput(
  input: string,
  name: string = "",
): ParseResult {
  if (!input.trim()) {
    return { format: "plain", sequences: [], errors: [] };
  }

  if (isFasta(input)) {
    return parseFasta(input);
  }

  return parsePlain(input, name);
}
